<template>
    <b-card
        header-tag="h4"
        :header="uLang('app.inspect_action_documents')"
        class="action-documents-table bg-default-card"
    >
        <rea-loader :loading="loading"></rea-loader>

        <div class="d-flex align-items-center mt-3" v-if="hasActionDocumentChecks">
            <span class="mr-2">{{ uLang('app.rows_per_page_text') }}:</span>
            <select class="rowsPerPageClass" v-model.number="tablePageSize">
                <option :value="15">15</option>
                <option :value="30">30</option>
                <option :value="45">45</option>
                <option :value="60">60</option>
                <option :value="90">90</option>
            </select>
        </div>

        <table class="action-documents-table table mt-2">
            <tr v-if="!hasActionDocumentChecks">
                <td colspan="4">{{ uLang('app.empty_table') }}</td>
            </tr>
            <tr class="table-heading" v-if="actionDocumentChecks.length > 0">
                <th class="w5"></th>
                <th class="w10">{{ uLang('app.candidate') }}</th>
                <th class="w30">{{ uLang('app.trajectory') }}</th>
                <th class="w30">{{ uLang('app.trajectory_manager_literal') }}</th>
                <th class="w10">{{ uLang('app.rapport') }}</th>
                <th class="w10">{{ uLang('app.submitted_at') }}</th>
            </tr>
            <tr v-if="actionDocumentChecks.length > 0">
                <td></td>
                <td colspan="2"></td>
                <td>
                    <input
                        type="text"
                        class="form-control"
                        :placeholder="uLang('app.search_by_trajectory_manager')"
                        v-model="filters.trajectory_manager"
                    />
                </td>
            </tr>
            <tr v-for="actionDocumentCheck in pagedDocuments" v-if="actionDocumentCheck.action_progress !== null">
                <td>
                    <i class="ti-email pointer"
                       @click.prevent="sendLogToRelation(actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.relation)"
                    ></i>
                </td>
                <td>
                    <name-telephone-field
                        :relation="actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.relation"
                    />
                </td>
                <td>
                    <span v-if="$hasRole('contact')">
                        {{ actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.trajectory.name }}
                    </span>
                    <span v-else>
                        <router-link
                            :to="trajectoryLink(actionDocumentCheck)"
                            v-text="actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.trajectory.name"
                            target="_blank"
                        />
                    </span>
                </td>
                <td>
                    <name-telephone-field
                        :relation="actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.trajectory_manager"
                    />
                </td>
                <td>
                    <router-link
                        :to="actionDocumentLink(actionDocumentCheck)"
                        v-text="actionDocumentCheck.action_progress.action_document_version.action_document.title"
                        target="_blank"
                    />
                </td>
                <td>{{ actionDocumentCheck.created_at | date }}</td>
            </tr>
        </table>

        <div class="VuePagination" v-if="hasActionDocumentChecks && filteredActionDocumentChecks.length > 0">
            <ul class="pagination VuePagination__pagination" v-show="totalPages > 1">
                <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-prev-chunk"
                    :class="{ disabled: currentChunk <= 1 }">
                    <a class="page-link" href="javascript:void(0);" @click="goPrevChunk">&lt;&lt;</a>
                </li>
                <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-prev-page"
                    :class="{ disabled: tablePage === 0 }">
                    <a class="page-link" href="javascript:void(0);" @click="tablePage--">&lt;</a>
                </li>
                <li v-for="p in chunkPageNumbers" :key="p"
                    class="VuePagination__pagination-item page-item"
                    :class="{ active: tablePage === p - 1 }">
                    <a class="page-link" role="button" @click="tablePage = p - 1">{{ p }}</a>
                </li>
                <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-next-page"
                    :class="{ disabled: tablePage >= totalPages - 1 }">
                    <a class="page-link" href="javascript:void(0);" @click="tablePage++">&gt;</a>
                </li>
                <li class="VuePagination__pagination-item page-item VuePagination__pagination-item-next-chunk"
                    :class="{ disabled: currentChunk >= totalChunks }">
                    <a class="page-link" href="javascript:void(0);" @click="goNextChunk">&gt;&gt;</a>
                </li>
            </ul>
            <p class="VuePagination__count">{{ paginationCount }}</p>
        </div>

        <b-modal
            class="log-modal-lg"
            :close-on-esc="false"
            :close-on-backdrop="false"
            :hideFooter="true"
            :title="uLang('app.add_type', { type: uLang('app.log') })"
            :key="log_form_key"
            ref="relationLogsModal"
            size="lg"
            v-if="($can('create-intern-logs')
                    || $can('update-intern-logs')
                    || $can('create-report-logs')
                    || $can('update-report-logs')
                    || $can('create-candidate-logs')
                    || $can('update-candidate-logs'))
            && relation_id !== null"
        >
            <log-form
                @logCreated="handleLogCreated"
                @categoryChanged="handleCategoryChanged"
                entityType="relations"
                :entityId="relation_id"
                :overrideEndpoint="logsFormApiUrl"
            />
        </b-modal>
    </b-card>
</template>
<script>
import {LogForm} from '~/components/forms'
import {ReaLoader} from '~/components/helpers'
import MultiSelect from 'vue-multiselect'
import NameTelephoneField from "../../../elements/name-telephone-field";
import {debounce} from "lodash";
import moment from "moment";
import {DashboardFilter} from '~/mixins'

export default {
    name: 'InspectableActionDocuments',

    components: {
        NameTelephoneField,
        ReaLoader, MultiSelect,
        LogForm
    },

    props: {
        actionDocumentChecks: {
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },

    mixins: [
        DashboardFilter,
    ],

    data() {
        return {
            filteredActionDocumentChecks: [],
            tablePage: 0,
            tablePageSize: 15,
        }
    },

    computed: {
        hasActionDocumentChecks() {
            return !_.isEmpty(this.actionDocumentChecks)
        },
        pagedDocuments() {
            const start = this.tablePage * this.tablePageSize;
            return this.filteredActionDocumentChecks.slice(start, start + this.tablePageSize);
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.filteredActionDocumentChecks.length / this.tablePageSize));
        },
        paginationStart() {
            return this.filteredActionDocumentChecks.length === 0
                ? 0 : this.tablePage * this.tablePageSize + 1;
        },
        paginationEnd() {
            return Math.min((this.tablePage + 1) * this.tablePageSize, this.filteredActionDocumentChecks.length);
        },
        currentPage() { return this.tablePage + 1; },
        currentChunk() { return Math.ceil(this.currentPage / 5); },
        totalChunks() { return Math.ceil(this.totalPages / 5); },
        chunkStart() { return (this.currentChunk - 1) * 5 + 1; },
        chunkPageNumbers() {
            const count = this.chunkStart + 5 <= this.totalPages ? 5 : this.totalPages - this.chunkStart + 1;
            return Array.from({ length: count }, (_, i) => this.chunkStart + i);
        },
        paginationCount() {
            const total = this.filteredActionDocumentChecks.length;
            if (total === 0) return '';
            if (total === 1) return '1 resultaat';
            if (this.totalPages === 1) return `${total} resultaten`;
            return `${this.paginationStart} tot ${this.paginationEnd} van de ${total} resultaten`;
        },
    },
    methods: {
        applyAllFilters() {
            this.tablePage = 0;
            let docs = (Array.isArray(this.actionDocumentChecks)
                ? this.actionDocumentChecks
                : Object.values(this.actionDocumentChecks)
            ).filter(d => d.action_progress !== null);

            if (this.filters.trajectory_manager) {
                const needle = this.filters.trajectory_manager.toLowerCase();
                docs = docs.filter(d =>
                    (d.action_progress.milestone_progress.trajectory_enrolment.trajectory_manager?.full_name || '')
                        .toLowerCase().includes(needle)
                );
            }

            this.filteredActionDocumentChecks = docs;
        },
        goPrevChunk() {
            if (this.currentChunk > 1) this.tablePage = (this.currentChunk - 2) * 5;
        },
        goNextChunk() {
            if (this.currentChunk < this.totalChunks) this.tablePage = this.currentChunk * 5;
        },
        relationLink(actionDocumentCheck) {
            return {
                name: 'admin.relations.view',
                params: {'id': actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.relation.id}
            }
        },
        trajectoryLink(actionDocumentCheck) {
            return {
                name: 'admin.relations.trajectory-enrolments.view',
                params: {
                    'relationId': actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.relation.id,
                    'trajectoryEnrolmentId': actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.id,
                }
            }
        },
        trajectoryManagerLink(actionDocumentCheck) {
            return {
                name: 'admin.relations.view',
                params: {
                    'id': actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.trajectory_manager.id,
                }
            }
        },
        actionDocumentLink(actionDocumentCheck) {
            return {
                name: 'admin.relations.trajectory-enrolments.milestone-progresses.action-progresses.action-document.check',
                params: {
                    'relationId': actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.relation.id,
                    'trajectoryEnrolmentId': actionDocumentCheck.action_progress.milestone_progress.trajectory_enrolment.id,
                    'milestoneProgressId': actionDocumentCheck.action_progress.milestone_progress.id,
                    'actionProgressId': actionDocumentCheck.action_progress.id,
                    'checkId': actionDocumentCheck.id,
                }
            }
        },

    },

    watch: {
        actionDocumentChecks() { this.applyAllFilters(); },
        'filters.trajectory_manager': debounce(function () { this.applyAllFilters(); }, 500),
        tablePageSize() { this.tablePage = 0; },
    },

    mounted() {
        this.applyAllFilters();
    }
}
</script>
<style lang="scss">
    .rowsPerPageClass {
        display: inline-block;
        width: 80px;
        padding: 4px 6px;
    }

    table.action-documents-table {
        tr:first-child {
            td, th {
                text-align: left;
                border-top: none;
            }
        }
        td, th {
            padding-left: 0px !important;
        }

    }
    .action_icon {
        color: #5A4587 !important;
    }

    .action_icon:hover {
        color: lighten(#5A4587, 25%) !important;
    }

    .card-block {
        position: relative;
        min-height: 75px;
    }

    .override-th-min-width {
        min-width: 10px;
    }

    .table-heading {
        width: 100%;
    }

    .w5 {
        width: 5%
    }

    .w10 {
        width: 10%
    }

    .w30 {
        width: 30%
    }
</style>
